import React, { useState, useEffect } from 'react';
import { api } from '../api';
import './PnlCalendar.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatPnl = (val: number) => {
  const sign = val >= 0 ? '+' : '';
  return `${sign}$${Math.abs(val).toFixed(2)}`;
};

const PnlCalendar: React.FC = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [days, setDays] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getDailyPnl(month, year)
      .then(res => setDays(res.days || {}))
      .catch(() => setDays({}))
      .finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  // getDay() returns 0=Sun, we want 0=Mon
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const totalPnl = Object.values(days).reduce((sum, v) => sum + v, 0);
  const profitDays = Object.values(days).filter(v => v > 0).length;
  const lossDays = Object.values(days).filter(v => v < 0).length;

  const cells: { day: number | null; pnl: number | null }[] = [];
  // Empty cells before first day
  for (let i = 0; i < startDow; i++) cells.push({ day: null, pnl: null });
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const pnl = days[dateStr] ?? null;
    cells.push({ day: d, pnl });
  }

  const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();
  const today = now.getDate();

  return (
    <div className="pnl-calendar">
      <div className="pnl-cal-header">
        <div className="pnl-cal-nav">
          <button className="pnl-cal-nav-btn" onClick={prevMonth}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <h3 className="pnl-cal-title">{MONTH_NAMES[month - 1]} {year}</h3>
          <button className="pnl-cal-nav-btn" onClick={nextMonth}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
        <div className="pnl-cal-summary">
          <span className={`pnl-cal-total ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
            {formatPnl(totalPnl)}
          </span>
          <span className="pnl-cal-meta">
            {profitDays > 0 && <span className="pnl-cal-win">{profitDays}W</span>}
            {lossDays > 0 && <span className="pnl-cal-loss">{lossDays}L</span>}
          </span>
        </div>
      </div>

      <div className="pnl-cal-grid">
        {DAY_LABELS.map(d => (
          <div key={d} className="pnl-cal-dow">{d}</div>
        ))}
        {cells.map((cell, i) => (
          <div
            key={i}
            className={`pnl-cal-cell${cell.day === null ? ' pnl-cal-cell--empty' : ''}${cell.pnl !== null && cell.pnl > 0 ? ' pnl-cal-cell--profit' : ''}${cell.pnl !== null && cell.pnl < 0 ? ' pnl-cal-cell--loss' : ''}${isCurrentMonth && cell.day === today ? ' pnl-cal-cell--today' : ''}`}
          >
            {cell.day !== null && (
              <>
                <span className="pnl-cal-day">{cell.day}</span>
                {cell.pnl !== null && (
                  <span className={`pnl-cal-amount ${cell.pnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatPnl(cell.pnl)}
                  </span>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {loading && <div className="pnl-cal-loading">Loading...</div>}
    </div>
  );
};

export default PnlCalendar;
